import { z } from 'zod';
import { UuidSchema } from './ids';

const AndNode = z.object({ node_type: z.literal('and'), node_id: UuidSchema, child_node_ids: z.array(UuidSchema).min(1) }).strict();
const OrNode = z.object({ node_type: z.literal('or'), node_id: UuidSchema, child_node_ids: z.array(UuidSchema).min(1) }).strict();
const NotNode = z.object({ node_type: z.literal('not'), node_id: UuidSchema, child_node_id: UuidSchema }).strict();
const TextEqualsNode = z.object({ node_type: z.literal('text_equals'), node_id: UuidSchema, block_id: UuidSchema, value: z.string().max(2048) }).strict();
const TextContainsNode = z.object({ node_type: z.literal('text_contains'), node_id: UuidSchema, block_id: UuidSchema, value: z.string().min(1).max(2048) }).strict();
const TextLengthMinNode = z.object({ node_type: z.literal('text_length_min'), node_id: UuidSchema, block_id: UuidSchema, value: z.number().int().nonnegative() }).strict();
const TextLengthMaxNode = z.object({ node_type: z.literal('text_length_max'), node_id: UuidSchema, block_id: UuidSchema, value: z.number().int().nonnegative() }).strict();
const OptionSelectedNode = z.object({ node_type: z.literal('option_selected'), node_id: UuidSchema, block_id: UuidSchema, option_id: UuidSchema }).strict();
const OptionNotSelectedNode = z.object({ node_type: z.literal('option_not_selected'), node_id: UuidSchema, block_id: UuidSchema, option_id: UuidSchema }).strict();
const CheckboxCountMinNode = z.object({ node_type: z.literal('checkbox_count_min'), node_id: UuidSchema, block_id: UuidSchema, value: z.number().int().nonnegative() }).strict();
const CheckboxCountMaxNode = z.object({ node_type: z.literal('checkbox_count_max'), node_id: UuidSchema, block_id: UuidSchema, value: z.number().int().nonnegative() }).strict();
const HiddenValueEqualsNode = z.object({ node_type: z.literal('hidden_value_equals'), node_id: UuidSchema, block_id: UuidSchema, value: z.string().max(2048) }).strict();

export const ConditionNodeSchema = z.discriminatedUnion('node_type', [
  AndNode, OrNode, NotNode,
  TextEqualsNode, TextContainsNode, TextLengthMinNode, TextLengthMaxNode,
  OptionSelectedNode, OptionNotSelectedNode,
  CheckboxCountMinNode, CheckboxCountMaxNode,
  HiddenValueEqualsNode,
]);
export type ConditionNode = z.infer<typeof ConditionNodeSchema>;

export const COMPOUND_NODE_TYPES = new Set(['and', 'or', 'not']);
export const PREDICATE_NODE_TYPES = new Set([
  'text_equals', 'text_contains', 'text_length_min', 'text_length_max',
  'option_selected', 'option_not_selected', 'checkbox_count_min', 'checkbox_count_max',
  'hidden_value_equals',
]);

const ShowBlockAction = z.object({ action_type: z.literal('show_block'), block_id: UuidSchema }).strict();
const HideBlockAction = z.object({ action_type: z.literal('hide_block'), block_id: UuidSchema }).strict();
const JumpToPageAction = z.object({ action_type: z.literal('jump_to_page'), page_id: UuidSchema }).strict();
const EndFormAction = z.object({ action_type: z.literal('end_form') }).strict();

export const LogicActionSchema = z.discriminatedUnion('action_type', [
  ShowBlockAction, HideBlockAction, JumpToPageAction, EndFormAction,
]);
export type LogicAction = z.infer<typeof LogicActionSchema>;

export const LogicRuleSchema = z
  .object({
    logic_rule_id: UuidSchema,
    condition_root_node_id: UuidSchema,
    priority: z.number().int().nonnegative(),
    action: LogicActionSchema,
  })
  .strict();
export type LogicRule = z.infer<typeof LogicRuleSchema>;